import { useConnectorVersions } from "hooks/useConnectorVersions";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import SoftBox from "components/SoftBox";
import Version from "./components/Version";
import { useResourceListener } from "hooks/useResourceListener";
import NoDataIcon from "layouts/Flow Designer/components/NoDataIcon";
import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useReactFlow } from "reactflow";
import { toast } from "react-toastify";
import { applyConnectorSchema, fetchConnectorSchema, getConnectorSelectedNode, isConnectorSchemaCurrent } from "helpers/connectorSchema";
import { setSelectedNode, updateComponentPropsTab } from "redux/actions/flowDesigner/componentPropsPanelActionCreator";
import useAllowEditWorkflow from "hooks/useAllowEditWorkflow";
import { useDetermineShareFlowUserAccessType } from "hooks/useDetermineShareFlowUserAccessType";

const ComponentVersions = ({ selectedNode, previousSelectedNodeId, autoSaveFlow }) => {
	const dispatch = useDispatch();
	const reactFlowInstance = useReactFlow();
	const { getNode, setNodes } = reactFlowInstance;
	const tabId = `componentProps-${selectedNode?.elementId}`;
	const isDirty = useSelector((store) => store.designer.componentPropsPanel.tabs[tabId]?.isDirty);
	const { currentSavedFlow } = useSelector((store) => store.designer.flows);
	const { allowEditWorkflow } = useAllowEditWorkflow();
	const { isReadOnlyAccess } = useDetermineShareFlowUserAccessType(currentSavedFlow?.id);
	const { connectorVersions, refetchConnectorVersions } = useConnectorVersions(
		selectedNode?.connectorId
	);
	const activeVersion = connectorVersions?.find(
		(version) => version?.version === selectedNode?.version
	);
	useResourceListener("connectorsVersionUpdate", refetchConnectorVersions);

	useEffect(() => {
		if (!activeVersion || isDirty) return;

		const currentNode = getNode(selectedNode.elementId);

		if (!currentNode || currentNode.data.version !== activeVersion.version || isConnectorSchemaCurrent(currentNode.data, activeVersion)) return;

		let cancelled = false;
		const refreshSchema = async () => {
			try {
				const schema = await fetchConnectorSchema(activeVersion.config_files);

				if (cancelled) return;

				const node = getNode(selectedNode.elementId);

				if (!node || node.data.version !== activeVersion.version) return;

				const updatedNode = applyConnectorSchema(node, schema, activeVersion.config_files, activeVersion.version, activeVersion.updated_datetime);
				const canSave = currentSavedFlow?.shared_status !== "active" && allowEditWorkflow(isReadOnlyAccess);
				
				if (canSave) setNodes((nodes) => nodes.map((current) => current.id === node.id ? updatedNode : current));

				dispatch(updateComponentPropsTab(tabId, updatedNode.data.flowElement));
				dispatch(setSelectedNode({
					node: getConnectorSelectedNode(updatedNode),
					tabId
				}));
				if (canSave) autoSaveFlow(reactFlowInstance);
			} catch (error) {
				if (!cancelled) toast.error(`Unable to refresh connector configuration: ${error.message}`);
			}
		};

		refreshSchema();

		return () => { cancelled = true; };
	}, [activeVersion, selectedNode?.elementId, isDirty]);

	const renderVersions = () => {
		if (connectorVersions && connectorVersions.length === 0) {
			return (
				<Box sx={{ p: 3, height: "80%", display: "flex", alignItems: "center", justifyContent: "center" }}>
					<NoDataIcon
						message="This connector needs to be uploaded first before it can be used and show versions."
						variant="body2"
						styles={{ width: "64px", height: "64px" }}
					/>
				</Box>
			);
		}

		if (connectorVersions?.length) {
			return connectorVersions
				.sort((a, b) => b?.version - a?.version)
				.map((version) => (
					<SoftBox
						key={version?.id}
						sx={{ marginBottom: "12px" }}
					>
						<Version
							key={version?.id}
							version={version}
							selectedNode={selectedNode}
							previousSelectedNodeId={previousSelectedNodeId}
							activeVersion={activeVersion}
						/>
						<SoftBox padding="0 4px">
							<Divider className="component-props-divider" />
						</SoftBox>
					</SoftBox>
				));
		}
	};

	return (
		<>
			<Box sx={{ height: "100%", overflowY: "auto" }}>
				{renderVersions()}
			</Box>
		</>
	);
};

export default ComponentVersions;
